import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { useElementActions } from 'bom/hooks';
import { IElement } from 'bom/types';
import { FormProvider, useNotificationContext } from 'core/states';

import { upsertElementSchema } from './schema';
import { IUpsertElementFormData } from './types';
import { UpsertElementFormFields } from './UpsertElementFormFields';

interface IProps {
  jobId: number;
  element?: Partial<IElement>;
  defaultValues?: Partial<IUpsertElementFormData>;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: (updatedElement: IElement) => void;
}

export const UpsertElement = ({
  jobId,
  element,
  defaultValues: initialDefaultValues,
  isOpen,
  onClose,
  onSubmitSuccess,
}: IProps) => {
  const { clearNotification } = useNotificationContext();

  const { upsertElement } = useElementActions();

  const isEdit = useMemo(() => !!element?.id, [element?.id]);

  const defaultValues = useMemo(() => {
    if (!isEdit) {
      return {
        ...initialDefaultValues,
        elementName: initialDefaultValues?.element?.name,
      };
    }

    return {
      ...initialDefaultValues,
      element: {
        name: element.name,
      },
      elementName: element.name,
      quantity: element.quantity,
    };
  }, [element?.name, element?.quantity, isEdit, initialDefaultValues]);

  const onSubmit =
    (reset: UseFormReset<IUpsertElementFormData>) => async (data: IUpsertElementFormData) => {
      const idPayload = isEdit ? { id: element.id as number } : { bomId: jobId };

      const { success, element: updatedElement } = await upsertElement({
        element: {
          ...idPayload,
          ...(data.useElementTemplate && { elementTemplateId: data.elementTemplate?.id }),
          name: data.element?.name,
          quantity: data.quantity,
        },
        ...(!isEdit && { searchByName: 0 }),
        ...(isEdit && { includeCost: 1 }),
      });

      if (!success) {
        return;
      }

      setTimeout(() => {
        handleOnClose(reset);
        onSubmitSuccess?.(updatedElement);
      }, 1000);
    };

  const handleOnClose = (reset?: UseFormReset<IUpsertElementFormData>) => {
    reset?.(defaultValues);
    onClose();

    clearNotification();
  };

  return (
    <FormProvider schema={upsertElementSchema()} defaultValues={defaultValues} resetDefaultValues>
      <UpsertElementFormFields
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        isEdit={isEdit}
      />
    </FormProvider>
  );
};
